// ═══════════════════════════════════════════════════════════════════════════
// datosConexionTn — lo que se MUESTRA de la conexión de una tienda (sólo leer)
// ───────────────────────────────────────────────────────────────────────────
// 30-sep-2026, pedido de Mariano: ver qué plan de Tienda Nube tiene cada
// tienda ("Plan: Impulso") arriba de sus dashboards. El plan ya lo guarda cada
// sincronización en `conexion.metadata.planTienda` (el CÓDIGO que devuelve
// `store.plan_name`, ej. 'AR-plan-B'); acá sólo se le pone nombre. En pantalla
// va SIEMPRE el nombre, nunca el código (Mariano: "que no pongamos el código
// sino lo que es"); el código queda en la ayuda del cartel.
//
// El agregador neutro (`core/lib/ajustesConexionRegistro`, `getDatosConexion`)
// los junta por `plataforma`, igual que los ajustes y los dashboards.
//
// OJO: esto es SÓLO el cartel. Las comisiones de Rentabilidad salen del
// catálogo del servidor, que sigue aceptando únicamente códigos medidos.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Código de plan → nombre comercial, VERIFICADO contra una tienda real.
 * ESPEJO del catálogo de tarifas del servidor
 * (`functions/syncAdapters/canonico/tienda-nube/tarifasTnSeed.js`, `planes`).
 */
export const PLANES_TN = Object.freeze({
  'AR-plan-B': 'Impulso',
})

/**
 * Códigos DEDUCIDOS, sin verificar: Tienda Nube no publica qué código es cada
 * plan. La B es Impulso (medido); los planes pagos van Esencial → Impulso →
 * Escala, así que la A y la C se deducen por el orden. Se muestran con "(a
 * confirmar)" y pasan a `PLANES_TN` cuando una tienda con ese código lo
 * confirme (la C apareció en una tienda real el 30-sep-2026).
 */
export const PLANES_TN_DEDUCIDOS = Object.freeze({
  'AR-plan-A': 'Esencial',
  'AR-plan-C': 'Escala',
})

const propio = (mapa, clave) => (Object.prototype.hasOwnProperty.call(mapa, clave) ? mapa[clave] : null)

/**
 * PURA. Lo que se sabe del plan de un código.
 * @returns {{ codigo: string, nombre: string|null, verificado: boolean }|null} `null` si no hay dato
 */
export function planDeTiendaTn(codigo) {
  if (typeof codigo !== 'string' || codigo.trim() === '') return null
  const c = codigo.trim()
  const verificado = propio(PLANES_TN, c)
  if (verificado) return { codigo: c, nombre: verificado, verificado: true }
  return { codigo: c, nombre: propio(PLANES_TN_DEDUCIDOS, c), verificado: false }
}

/** PURA. El texto del cartel: el nombre, nunca el código. `null` si no hay dato. */
export function nombreDePlanTn(codigo) {
  const p = planDeTiendaTn(codigo)
  if (!p) return null
  if (p.verificado) return p.nombre
  return p.nombre ? `${p.nombre} (a confirmar)` : 'sin identificar'
}

/** PURA. La ayuda del cartel: qué es el dato y cuán firme es el nombre. */
export function ayudaDePlanTn(codigo) {
  const base = 'El plan que la tienda tiene contratado en Tienda Nube, leído en la última sincronización.'
  const p = planDeTiendaTn(codigo)
  if (!p) return base
  if (p.verificado) return `${base} Código de Tienda Nube: ${p.codigo}.`
  if (p.nombre) return `${base} Tienda Nube lo informa con el código ${p.codigo}; el nombre está deducido por el orden de los planes y falta confirmarlo con la tienda.`
  return `${base} Tienda Nube lo informa con el código ${p.codigo}, que todavía no sabemos a qué plan corresponde.`
}

export const DATOS_CONEXION_TN = Object.freeze([
  Object.freeze({
    clave: 'plan',
    plataforma: 'tienda-nube',
    etiqueta: 'Plan de Tienda Nube',
    ayuda: (conexion) => ayudaDePlanTn(conexion?.metadata?.planTienda),
    valor: (conexion) => nombreDePlanTn(conexion?.metadata?.planTienda),
  }),
])
